// Relative path: src/components/Recommendations/FeaturedReviews.js

import React, { useState, useEffect } from 'react';
import FeaturedReviewCard from './FeaturedReviewCard';
import DetailsPopup from '../Popup/DetailsPopup';
import ReactDOM from 'react-dom';
import recUtils from '../../utils/RecUtils';
import websiteUtils from '../../utils/WebsiteUtils';

/**
 * Featured Reviews Component
 * Displays 1-2 featured reviews with before/after photos
 * Shows 2 reviews on desktop, 1 on mobile due to horizontal restrictions
 */
const FeaturedReviews = ({
  clinicName = '',
  formData = {},
  isMobile = false,
  className = ""
}) => {
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllOnMobile, setShowAllOnMobile] = useState(false);
  const [isReviewsInfoOpen, setIsReviewsInfoOpen] = useState(false);

  // Mock content for the reviews info popup
  // real patient, from interent
  const reviewsInfoContent = [
    "Our system automatically collects reviews written by real patients from the internet including Reddit, Youtube and Tiktok. We select reviews from patients with similar profiles to yours to help you make the most informed decision.",
    "With our AI, user ratings are derived from sentiment analysis on each reviews content in order to create a consistent and user independent rating system.",
    "Each review includes before and after photos, detailed experiences, and generated satisfaction ratings. Reviews that are paid or sponsored are eliminated by the algorithm.",
    "All external reviews are linked to their original sources, while internal reviews can be viewed in detail on our platform. We maintain strict verification standards to ensure authenticity."
  ];

  useEffect(() => {
    const loadFeaturedReviews = async () => {
      if (!clinicName) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const reviews = await recUtils.fetchFeaturedReviewsForClinic(clinicName, formData);
        
        // Resolve logos for each review using centralized websiteUtils
        const reviewsWithLogos = reviews.map(review => ({
          ...review,
          sourceLogo: review.websiteInfo ? websiteUtils.getLogo(review.websiteInfo.websiteKey) : websiteUtils.getLogo('match medic')
        }));
        
        setFeaturedReviews(reviewsWithLogos);
      } catch (err) {
        console.error('FEATURED REVIEWS: Error loading featured reviews:', err);
        setFeaturedReviews([]);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedReviews();
  }, [clinicName, formData]);

  // Handle "see more" button click
  const handleSeeMore = () => {
    setShowAllOnMobile(true);
  };

  // Handle review card click - improved URL logic
  const handleReviewClick = (review) => {
    // Determine the correct URL to open based on is_external flag and external_url availability
    let urlToOpen;
    
    const isExternal = review.originalData.is_external !== false;
    const hasValidExternalUrl = review.originalData.external_url && 
                               review.originalData.external_url !== '#' && 
                               review.originalData.external_url.trim() !== '';

    if (isExternal && hasValidExternalUrl) {
      // External review - use the external URL
      urlToOpen = review.originalData.external_url;
    } else {
      // Internal review - use our website URL with /reviews/ path
      urlToOpen = `/review/${review.id}`;
    }
    
    if (urlToOpen) {
      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    }
  };

  // Create a portal for the reviews info popup
  const renderReviewsInfoPopup = () => {
    if (!isReviewsInfoOpen) return null;
    
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(
        <DetailsPopup 
          isOpen={isReviewsInfoOpen}
          onClose={() => setIsReviewsInfoOpen(false)}
          title="How Our Reviews Work"
          paragraphs={reviewsInfoContent}
        />,
        document.body
      );
    }
    
    return null;
  };

  // Don't render if loading, error, or no reviews
  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Featured Results
          </h3>
          <p className="underline text-sm font-bold text-gray-600 mb-3 cursor-pointer hover:text-gray-800">
            Learn how reviews work
          </p>
        </div>
        <div className={`grid gap-6 ${
          isMobile 
            ? 'grid-cols-1' 
            : 'grid-cols-1 md:grid-cols-2'
        }`}>
          {[...Array(isMobile ? 1 : 2)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 rounded-lg h-64" />
          ))}
        </div>
      </div>
    );
  }

  // Hide component completely if no reviews (production behavior)
  if (featuredReviews.length === 0) {
    return null;
  }

  // Determine how many reviews to show based on screen size and mobile state
  let reviewsToShow;
  if (isMobile) {
    reviewsToShow = showAllOnMobile ? featuredReviews : featuredReviews.slice(0, 1);
  } else {
    reviewsToShow = featuredReviews.slice(0, 2);
  }

  // Check if we should show "see more" button on mobile
  const shouldShowSeeMore = isMobile && !showAllOnMobile && featuredReviews.length > 1;

  return (
    <div className={`w-full ${className}`}>
      {/* Section Header with Clickable Subtitle */}
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Featured Results
        </h3>
        <p 
          className="underline font-bold text-sm text-gray-400 cursor-pointer hover:text-gray-600 transition-colors duration-200"
          onClick={() => setIsReviewsInfoOpen(true)}
        >
          Learn how reviews work
        </p>
      </div>

      {/* Reviews Grid */}
      <div className={`grid gap-6 ${
        isMobile 
          ? 'grid-cols-1' 
          : reviewsToShow.length === 1 
            ? 'grid-cols-1 max-w-md' 
            : 'grid-cols-1 md:grid-cols-2'
      }`}>
        {reviewsToShow.map((review) => (
          <FeaturedReviewCard
            key={review.id}
            reviewerName={review.reviewerName}
            reviewerAvatar={review.reviewerAvatar}
            sourceName={review.sourceName}
            sourceLogo={review.sourceLogo}
            beforeImage={review.beforeImage}
            afterImage={review.afterImage}
            rating={review.rating}
            maxRating={review.maxRating}
            tags={review.tags}
            reviewText={review.reviewText}
            highlightText={review.highlightText}
            onClick={() => handleReviewClick(review)}
            className="h-full"
          />
        ))}
      </div>

      {/* See More Button - Only shown on mobile when there are more reviews */}
      {shouldShowSeeMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleSeeMore}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <svg 
              className="w-4 h-4 mr-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M19 9l-7 7-7-7"
              />
            </svg>
            See More ({featuredReviews.length - 1} more)
          </button>
        </div>
      )}

      {/* Reviews Info Popup */}
      {renderReviewsInfoPopup()}
    </div>
  );
};

export default FeaturedReviews;